'use client';

import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';

interface BoardCoverProps {
  /** Board id or name — used to seed a unique-but-deterministic cover. */
  seed: string;
  /** Dominant brand hue. */
  hue: string;
  /** Display initials fallback, e.g. 'BR' for "The Boardroom". */
  initials: string;
  /** Optional explicit image URL. If set, it replaces the procedural cover. */
  imageUrl?: string | null;
  className?: string;
  size?: number; // px. Square.
}

/**
 * Route an external image URL through `/api/img-thumb` so the bytes
 * the browser actually downloads match the rendered size. Each
 * cover is shown at ≤96 px on the densest layout (the lobby
 * directory grid), but the upstream Flaunch / IPFS originals are
 * 512×512+ JPEGs in the ~190 KB range. Asking the proxy for a 2×
 * device-pixel ratio thumbnail (`size * 2`, capped at 192) keeps
 * Retina sharpness while dropping the lobby's image transfer from
 * ~5 MB to ~0.25 MB on cold load.
 *
 * Same-origin paths, `data:` URIs, and `blob:` URIs are returned
 * verbatim — the proxy can't help (and would actively harm) those.
 */
function thumbnailUrl(
  src: string | null | undefined,
  renderedSize: number,
): string | null {
  if (!src) return null;
  if (src.startsWith('data:') || src.startsWith('blob:')) return src;
  if (src.startsWith('/')) return src;
  // Cap proxy width at 192 px — roughly twice the largest size any
  // current `BoardCover` consumer uses (96 in the directory grid)
  // so high-DPR screens stay sharp.
  const w = Math.min(192, Math.max(32, Math.round(renderedSize * 2)));
  return `/api/img-thumb?u=${encodeURIComponent(src)}&w=${w}`;
}

function hashString(str: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function xorshift(seed: number) {
  let s = seed || 1;
  return () => {
    s ^= s << 13;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 0xffffffff;
  };
}

/**
 * Procedural board cover — a mini stylised mosaic made from the board's hue.
 * At launch (v2), users will upload real metadata images. This keeps the lobby
 * expressive without needing real assets.
 */
export function BoardCover({
  seed,
  hue,
  initials,
  imageUrl,
  className,
  size = 56,
}: BoardCoverProps) {
  const rngSeed = useMemo(() => hashString(seed), [seed]);
  // If the provided image 404s / is a 1×1 transparent placeholder that
  // fails to decode, drop back to the procedural mosaic so users never
  // see a broken image or an empty square.
  const [imageFailed, setImageFailed] = useState(false);

  const cells = useMemo(() => {
    const rng = xorshift(rngSeed);
    const out: Array<{ x: number; y: number; op: number; tone: 'base' | 'shade' | 'highlight' }> = [];
    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 4; x++) {
        const n = rng();
        const op = 0.15 + n * 0.85;
        const tone: 'base' | 'shade' | 'highlight' = n > 0.78 ? 'highlight' : n > 0.4 ? 'base' : 'shade';
        out.push({ x, y, op, tone });
      }
    }
    return out;
  }, [rngSeed]);

  if (imageUrl && !imageFailed) {
    const optimizedSrc = thumbnailUrl(imageUrl, size) ?? imageUrl;
    return (
      <div
        className={cn('relative rounded-[3px] overflow-hidden border border-white/10', className)}
        style={{ width: size, height: size }}
      >
        <img
          src={optimizedSrc}
          alt=""
          width={size}
          height={size}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={() => setImageFailed(true)}
        />
      </div>
    );
  }

  return (
    <div
      className={cn('relative rounded-[3px] overflow-hidden border border-white/10', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
      >
        <defs>
          <radialGradient id={`bcov-bg-${rngSeed}`} cx="30%" cy="30%" r="75%">
            <stop offset="0%" stopColor={hue} stopOpacity="0.9" />
            <stop offset="60%" stopColor={hue} stopOpacity="0.2" />
            <stop offset="100%" stopColor="#070608" stopOpacity="1" />
          </radialGradient>
          <linearGradient id={`bcov-scan-${rngSeed}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="48%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="52%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="16" height="16" fill={`url(#bcov-bg-${rngSeed})`} />
        {cells.map((c, i) => {
          const fill =
            c.tone === 'highlight'
              ? '#fff'
              : c.tone === 'shade'
              ? '#090812'
              : hue;
          const op =
            c.tone === 'highlight' ? c.op * 0.35 : c.tone === 'shade' ? c.op * 0.4 : c.op * 0.55;
          return (
            <rect
              key={i}
              x={c.x * 4}
              y={c.y * 4}
              width={4}
              height={4}
              fill={fill}
              opacity={op}
            />
          );
        })}
        <rect x="0" y="0" width="16" height="16" fill={`url(#bcov-scan-${rngSeed})`} />
      </svg>
      <span
        aria-hidden
        className="absolute inset-0 flex items-end justify-start p-1 pointer-events-none"
      >
        <span
          className="text-[10px] font-mono tracking-[0.1em] font-bold text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
          style={{ textShadow: `0 0 6px ${hue}66, 0 1px 2px rgba(0,0,0,0.9)` }}
        >
          {initials}
        </span>
      </span>
    </div>
  );
}

export function boardInitials(name: string): string {
  const parts = name.replace(/[^a-z0-9\s]/gi, '').split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'B';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
